import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Skeleton,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  Lock as LockIcon,
  PlayArrow as PlayArrowIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import schedulerService, { SchedulerStatsPreview } from '../services/schedulerService';
import {
  next_shop_local_quarter_hour,
  shop_calendar_parts,
  shop_local_to_utc_ms,
  shop_today_ymd,
} from '../utils/shopTimezone';

/** Tomorrow 08:00 shop time — default when opening the scheduler dialog. */
function build_default_scheduler_start_date(): Date {
  const today = shop_today_ymd();
  const [y, mo, d] = today.split('-').map(Number);
  const tomorrow_ms = shop_local_to_utc_ms(y, mo, d, 0, 0, 0, 0) + 86400000;
  const tp = shop_calendar_parts(new Date(tomorrow_ms));
  return new Date(shop_local_to_utc_ms(tp.y, tp.mo, tp.d, 8, 0, 0, 0));
}

interface SchedulerStartDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (startDateTime: Date) => void;
  running: boolean;
}

function StatRow({
  label,
  value,
  loading,
  indent = false,
  bold = false,
  color,
}: {
  label: string;
  value: number | string;
  loading: boolean;
  indent?: boolean;
  bold?: boolean;
  color?: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pl: indent ? 3 : 0,
        py: 0.25,
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: color || 'text.secondary', fontWeight: bold ? 600 : 400 }}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton width={40} height={20} />
      ) : (
        <Typography
          variant="body2"
          sx={{ fontWeight: bold ? 700 : 500, color: color || 'text.primary' }}
        >
          {value}
        </Typography>
      )}
    </Box>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 700,
        color: 'text.disabled',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        mt: 2,
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

const SchedulerStartDialog: React.FC<SchedulerStartDialogProps> = ({
  open,
  onClose,
  onConfirm,
  running,
}) => {
  const [startDateTime, setStartDateTime] = useState<Date | null>(null);
  const [stats, setStats] = useState<SchedulerStatsPreview | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [showManualList, setShowManualList] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchStats = useCallback(async (dt: Date) => {
    setLoadingStats(true);
    setStatsError(null);
    try {
      const result = await schedulerService.getStatsPreview(dt);
      setStats(result);
    } catch (err: any) {
      setStatsError(err?.message || 'Failed to load stats');
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // On dialog open, set default and trigger initial stats fetch
  useEffect(() => {
    if (!open) return;
    const def = build_default_scheduler_start_date();
    setStartDateTime(def);
    setShowManualList(false);
    setStats(null);
    setStatsError(null);
    void fetchStats(def);
  }, [open, fetchStats]);

  const handleDateChange = (newValue: Date | null) => {
    if (!newValue || isNaN(newValue.getTime())) {
      setStartDateTime(newValue);
      return;
    }

    let adjusted = newValue;
    const pickedYmd = shop_today_ymd(newValue);
    if (pickedYmd === shop_today_ymd()) {
      const minAllowed = next_shop_local_quarter_hour();
      if (adjusted.getTime() < minAllowed.getTime()) {
        adjusted = minAllowed;
      }
    }

    setStartDateTime(adjusted);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchStats(adjusted);
    }, 400);
  };

  const handleConfirm = () => {
    if (startDateTime) {
      onConfirm(startDateTime);
    }
  };

  const minDateTime = next_shop_local_quarter_hour(new Date(Date.now() - 60000));

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={running ? undefined : onClose} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Typography variant="h6" component="span">
            Run Scheduler
          </Typography>
          {!running && (
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {/* Date/time picker */}
          <DateTimePicker
            label="Start from"
            value={startDateTime}
            onChange={handleDateChange}
            minDateTime={minDateTime}
            minutesStep={15}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                helperText: 'Orders scheduled before this time are frozen (untouched)',
              },
            }}
            disabled={running}
          />

          {statsError && (
            <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }} onClose={() => setStatsError(null)}>
              {statsError} — stats unavailable, you can still run.
            </Alert>
          )}

          {/* ── Scope ─────────────────────────────────── */}
          <SectionHeader>Scope</SectionHeader>
          <Divider sx={{ mb: 0.5 }} />
          <StatRow
            label="Total active orders"
            value={stats?.totalActiveOrders ?? 0}
            loading={loadingStats}
            bold
          />
          <StatRow
            label="Frozen (will not change)"
            value={stats?.fullyFrozenCount ?? 0}
            loading={loadingStats}
          />
          {(stats?.inProgressCount ?? 0) > 0 && (
            <StatRow
              label="↳ Currently in progress"
              value={stats?.inProgressCount ?? 0}
              loading={loadingStats}
              indent
            />
          )}
          <StatRow
            label="In scope (will reschedule)"
            value={stats?.inScopeCount ?? 0}
            loading={loadingStats}
            bold
          />
          {(stats?.partiallyFrozenCount ?? 0) > 0 && (
            <StatRow
              label="↳ Partially frozen (split across boundary)"
              value={stats?.partiallyFrozenCount ?? 0}
              loading={loadingStats}
              indent
            />
          )}

          {/* ── In-scope breakdown ───────────────────── */}
          <SectionHeader>In-scope breakdown</SectionHeader>
          <Divider sx={{ mb: 0.5 }} />
          <StatRow
            label="Auto-scheduled"
            value={stats?.autoScheduledCount ?? 0}
            loading={loadingStats}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 0.25,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Manually scheduled
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              {loadingStats ? (
                <Skeleton width={40} height={20} />
              ) : (
                <Typography variant="body2" fontWeight={500}>
                  {stats?.manuallyScheduledCount ?? 0}
                </Typography>
              )}
              {(stats?.manuallyScheduledCount ?? 0) > 0 && (
                <Button
                  size="small"
                  variant="text"
                  endIcon={showManualList ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setShowManualList((v) => !v)}
                  sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: 11 }}
                >
                  {showManualList ? 'Hide' : 'Show list'}
                </Button>
              )}
            </Stack>
          </Box>

          <Collapse in={showManualList && (stats?.manuallyScheduledOrders?.length ?? 0) > 0}>
            <Box
              sx={{
                ml: 3,
                mt: 0.5,
                mb: 0.5,
                borderLeft: '2px solid',
                borderColor: 'divider',
                pl: 1.5,
                maxHeight: 200,
                overflowY: 'auto',
              }}
            >
              {stats?.manuallyScheduledOrders?.map((o) => (
                <Box key={o.production_order_id} sx={{ py: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    #{o.production_order_id}{' '}
                    <Typography variant="caption" color="text.primary" component="span">
                      {o.product_name}
                    </Typography>
                    {'  '}
                    <Typography variant="caption" color="text.disabled" component="span">
                      {o.earliest_scheduled_date} {o.earliest_scheduled_time?.slice(0, 5)}
                      {o.due_date ? ` · due ${o.due_date.slice(0, 10)}` : ''}
                    </Typography>
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>

          <StatRow
            label="Not yet scheduled"
            value={stats?.unscheduledCount ?? 0}
            loading={loadingStats}
          />

          {/* ── Due-date urgency ─────────────────────── */}
          <SectionHeader>Due-date urgency (all active)</SectionHeader>
          <Divider sx={{ mb: 0.5 }} />
          <StatRow
            label="Overdue"
            value={stats?.overdueCount ?? 0}
            loading={loadingStats}
            color={(stats?.overdueCount ?? 0) > 0 ? 'error.main' : undefined}
          />
          <StatRow
            label="Due today"
            value={stats?.dueTodayCount ?? 0}
            loading={loadingStats}
            color={(stats?.dueTodayCount ?? 0) > 0 ? 'warning.main' : undefined}
          />
          <StatRow label="Due < 24h" value={stats?.dueWithin24hCount ?? 0} loading={loadingStats} />
          <StatRow
            label="Due this week"
            value={stats?.dueThisWeekCount ?? 0}
            loading={loadingStats}
          />
          <StatRow
            label="Due next 30 days"
            value={stats?.dueNext30DaysCount ?? 0}
            loading={loadingStats}
          />
          <StatRow label="No due date" value={stats?.noDueDateCount ?? 0} loading={loadingStats} />

          {/* ── Capacity ─────────────────────────────── */}
          <SectionHeader>Capacity</SectionHeader>
          <Divider sx={{ mb: 0.5 }} />
          <StatRow
            label="Est. hours to schedule"
            value={`${(stats?.totalEstimatedHoursInScope ?? 0).toFixed(1)}h`}
            loading={loadingStats}
            bold
          />

          {/* Frozen breakdown (informational, smaller) */}
          {((stats?.frozenAutoScheduledCount ?? 0) > 0 || (stats?.frozenManualCount ?? 0) > 0) && (
            <Box sx={{ mt: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <LockIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.disabled">
                  Frozen orders breakdown
                </Typography>
              </Box>
              <StatRow
                label="Auto-placed (locked in)"
                value={stats?.frozenAutoScheduledCount ?? 0}
                loading={loadingStats}
                indent
              />
              <StatRow
                label="Manually placed (locked in)"
                value={stats?.frozenManualCount ?? 0}
                loading={loadingStats}
                indent
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={running} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={running || !startDateTime || isNaN(startDateTime?.getTime())}
            startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
          >
            {running ? 'Running…' : 'Run Scheduler'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default SchedulerStartDialog;
